"use client";

import React, { useState, useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import styles from "./ChatBot.module.css";
// import chatbotIcon from "./ChatBot.webp";
import { StaticImage } from "gatsby-plugin-image";

const ChatBot = () => {
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 400;
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: "bot",
      html: "<strong>Hi, I am an AI chatbot. How can I help you today?</strong>",
    },
  ]);
  const [input, setInput] = useState("");
  const chatBodyRef = useRef(null);
  const messagesEndRef = useRef(null);
  const buttonRef = useRef(null);

  const sanitize = (html) => ({
    __html: DOMPurify.sanitize(html),
  });

  const parseMarkdown = (text) => {
    if (!text) return "";

    text = text.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    text = text.replace(/\n\n+/g, "</p><p>");
    text = text.replace(/\n/g, "<br>");
    text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    text = text.replace(
      /\[(.*?)\]\((.*?)\)/g,
      '<a href="$2" target="_blank">$1</a>'
    );

    // Handle ordered lists
    if (text.match(/^\d+\.\s+/m)) {
      let parts = text.split(/\n/);
      let inList = false;
      let listHTML = "";
      let result = "";

      parts.forEach((line) => {
        if (/^\d+\.\s+/.test(line)) {
          if (!inList) {
            listHTML = "<ol>";
            inList = true;
          }
          listHTML += "<li>" + line.replace(/^\d+\.\s+/, "") + "</li>";
        } else {
          if (inList) {
            listHTML += "</ol>";
            result += listHTML;
            inList = false;
            listHTML = "";
          }
          result += line + "<br>";
        }
      });

      if (inList) {
        listHTML += "</ol>";
        result += listHTML;
      }

      return `<p>${result}</p>`;
    }

    return `<p>${text}</p>`;
  };

  const getChatId = () => {
    let sessionId = sessionStorage.getItem("sessionId");
    if (!sessionId) {
      sessionId =
        "chat_" +
        (crypto.randomUUID
          ? crypto.randomUUID()
          : Math.random().toString(36).substr(2, 9));
      sessionStorage.setItem("sessionId", sessionId);
    }
    return sessionId;
  };
  const sendMessage = async () => {
    if (input.trim() === "") return;

    const userMessage = { type: "user", html: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    const typingMessage = { type: "typing", html: "" };
    setMessages((prev) => [...prev, typingMessage]);

    const currentSessionId = getChatId();

    try {
      const response = await fetch(
        "https://n8n.3cs.website/webhook/33c05414-66f0-489d-baad-2f7b7daf322a/chat",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: currentSessionId,
            chatInput: userMessage.html,
            route: "general",
          }),
        }
      );

      const data = await response.json();
      const botMessage = {
        type: "bot",
        html: parseMarkdown(
          data.output || "Sorry, I couldn't understand that."
        ),
      };

      setMessages((prev) => [
        ...prev.filter((m) => m.type !== "typing"),
        botMessage,
      ]);
    } catch (error) {
      console.error("Error:", error);
      const errorMsg = {
        type: "bot",
        html: "Sorry, there was an error processing your request. Please try again.",
      };
      setMessages((prev) => [
        ...prev.filter((m) => m.type !== "typing"),
        errorMsg,
      ]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      sendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Update the chat button visibility when chat is opened or closed
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      buttonRef.current.style.display = "none";
    } else if (buttonRef.current) {
      buttonRef.current.style.display = "flex";
    }
  }, [isOpen]);

  // Input field style for the circular rectangle appearance
  const inputStyle = {
    flex: 1,
    padding: "12px 16px",
    border: "1px solid #e0e0e0",
    borderRadius: "24px",
    outline: "none",
    fontSize: "14px",
    transition: "border 0.3s ease",
    fontFamily: "inherit",
    margin: 0,
  };

  // Make sure styles object exists before accessing properties
  const buttonClassName = styles ? styles.chatWidgetButton : "";
  const containerClassName = styles ? styles.chatWidgetContainer : "";
  const headerClassName = styles ? styles.chatWidgetHeader : "";
  const bodyClassName = styles ? styles.chatWidgetBody : "";
  const footerClassName = styles ? styles.chatWidgetFooter : "";
  const sendClassName = styles ? styles.chatWidgetSend : "";

  return (
    <>
      {/* Floating Chat Button */}
      <button
        ref={buttonRef}
        id="chat-widget-button"
        className={buttonClassName}
        onClick={() => setIsOpen(true)}
        aria-label="Open chat with support"
        style={{
          border: "none",
          position: "fixed",
          bottom: "18px",
          right: "15px",
          width: "44px",
          height: "44px",
          cursor: "pointer",
          zIndex: 1001,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.3s ease",
          padding: 0,
          margin: 0,
          backgroundColor: "transparent",
        }}
      >
        {/* <img
          src={chatbotIcon}
          alt="Chatbot Icon"
          width={57}
          height={57}
          loading="lazy"
        /> */}
        <StaticImage
          src="./ChatBot.webp"
          alt="Chatbot Icon"
          className="pop-up-icon"
          loading="lazy"
          width={44}
          height={44}
          fetchpriority="high"
          layout="fullWidth"
          formats={["auto", "webp", "avif"]}
        />
      </button>

      {isOpen && (
        <div
          className={containerClassName}
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            width: isMobile ? "90vw" : "380px",
            height: isMobile ? "70vh" : "530px",
            background: "white",
            borderRadius: "16px",
            boxShadow: "0 10px 25px rgba(34, 151, 155, 0.15)",
            display: "flex",
            flexDirection: "column",
            zIndex: 1000000,
            overflow: "hidden",
            transition: "all 0.3s ease",
            fontFamily:
              "'Geist Sans', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
          }}
        >
          <div
            className={headerClassName}
            style={{
              background: "#22979b",
              color: "#ffffff",
              padding: "18px 24px",
              fontWeight: 600,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "18px",
              borderRadius: "16px 16px 0 0",
            }}
          >
            <span>Talk to Sonali</span>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: "transparent",
                border: "none",
                color: "#ffffff",
                fontSize: "24px",
                cursor: "pointer",
                transition: "transform 0.2s ease",
                height: "30px",
                width: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                padding: 0,
                margin: 0,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          <div
            className={bodyClassName}
            ref={chatBodyRef}
            style={{
              flex: 1,
              padding: "20px",
              overflowY: "auto",
              backgroundColor: "#f9fafb",
              scrollBehavior: "smooth",
            }}
          >
            {messages.map((msg, i) =>
              msg.type === "typing" ? (
                <div
                  key={i}
                  className={
                    styles
                      ? `${styles.typingIndicator} ${styles.messageAnimation}`
                      : ""
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "14px 16px",
                    background: "#e6f7f8",
                    borderRadius: "14px",
                    borderTopLeftRadius: "4px",
                    marginBottom: "16px",
                    width: "fit-content",
                  }}
                >
                  <span
                    style={{
                      height: "8px",
                      width: "8px",
                      background: "#22979b",
                      borderRadius: "50%",
                      margin: "0 2px",
                      display: "inline-block",
                      opacity: 0.6,
                      animation: "bounce 1.2s infinite 0.1s",
                    }}
                  ></span>
                  <span
                    style={{
                      height: "8px",
                      width: "8px",
                      background: "#22979b",
                      borderRadius: "50%",
                      margin: "0 2px",
                      display: "inline-block",
                      opacity: 0.6,
                      animation: "bounce 1.2s infinite 0.3s",
                    }}
                  ></span>
                  <span
                    style={{
                      height: "8px",
                      width: "8px",
                      background: "#22979b",
                      borderRadius: "50%",
                      margin: "0 2px",
                      display: "inline-block",
                      opacity: 0.6,
                      animation: "bounce 1.2s infinite 0.5s",
                    }}
                  ></span>
                </div>
              ) : (
                <p
                  key={i}
                  className={
                    styles
                      ? `${styles[msg.type + "Message"]} ${
                          styles.messageAnimation
                        }`
                      : ""
                  }
                  style={{
                    marginBottom: "16px",
                    padding: "14px 16px",
                    borderRadius: "14px",
                    fontSize: "14px",
                    lineHeight: 1.5,
                    wordWrap: "break-word",
                    maxWidth: "85%",
                    marginTop: 0,
                    color: msg.type === "user" ? "#ffffff" : "#333333",
                    background: msg.type === "user" ? "#22979b" : "#e6f7f8",
                    borderTopRightRadius: msg.type === "user" ? "4px" : "14px",
                    borderTopLeftRadius: msg.type === "bot" ? "4px" : "14px",
                    marginLeft: msg.type === "user" ? "auto" : 0,
                    marginRight: msg.type === "user" ? 0 : "auto",
                  }}
                  dangerouslySetInnerHTML={sanitize(msg.html)}
                />
              )
            )}
            <div ref={messagesEndRef} />
          </div>

          <div
            className={footerClassName}
            style={{
              padding: "16px 20px",
              borderTop: "1px solid #eaeaea",
              display: "flex",
              gap: "12px",
              background: "white",
              alignItems: "center",
            }}
          >
            <input
              style={inputStyle}
              className={styles ? styles.chatWidgetInput : ""}
              type="text"
              placeholder="Type your message here..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
            />
            <button
              className={sendClassName}
              onClick={sendMessage}
              style={{
                background: "#22979b",
                color: "white",
                border: "none",
                padding: 0,
                borderRadius: "50%",
                cursor: "pointer",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                margin: 0,
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                alt-label="ChatBot Icon"
                role="img"
              >
                <path
                  d="M22 2L11 13"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M22 2L15 22L11 13L2 9L22 2Z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Add CSS keyframes for animations */}
      <style jsx>{`
        @keyframes bounce {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
            opacity: 1;
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default ChatBot;
